import React, { useEffect } from "react";
import { showOptionsByPark } from "../../data/showOptionsByPark";
import Tooltip from "./Tooltip";
import { getOptionAvailability, filterAvailableShows } from "../../utils/availabilityRules";

const ShowSelector = ({
  field,
  selectedParks,
  selected,
  formData,
  handleChange,
  showAllShows,
  setShowAllShows,
}) => {
  const strategyLabelToKey = {
    "Nighttime Spectaculars – Fireworks, projections, and big finales":
      "Nighttime Spectaculars",
    "Stage Shows – Broadway-style performances & musical revues": "Stage Shows",
    "Animal & Nature – Live animal shows and educational experiences":
      "Animal & Nature",
    "Cultural – World Showcase and themed entertainment": "Cultural",
    "Parades – Daytime/Evening character cavalcades and processionals":
      "Parades",
    "See it All – No preference, show me everything": "See it All",
  };

  const showStrategies = formData?.showFireworkStrategy?.showStrategy || [];
  const tripStart =
    formData?.basicInformation?.startDate && !isNaN(new Date(formData.basicInformation.startDate))
      ? new Date(formData.basicInformation.startDate)
      : null;

  const filterShowsByStrategy = (shows) => {
    if (showAllShows || showStrategies.length === 0) return shows;

    const selectedKeys = showStrategies
      .map((label) => strategyLabelToKey[label])
      .filter(Boolean);

    return shows.filter((s) =>
      selectedKeys.some((key) => s.showStrategyNote?.includes(key))
    );
  };

  const handleToggle = (name) => {
    const current = selected || [];
    const updated = current.includes(name)
      ? current.filter((n) => n !== name)
      : [...current, name];

    handleChange(field.id, updated);
  };

  useEffect(() => {
    const showsInForm = Array.isArray(selected) ? selected : [];
    if (showsInForm.length === 0) return;

    const { filtered } = filterAvailableShows(showsInForm, formData);
    if (filtered.length !== showsInForm.length) {
      handleChange(field.id, filtered);
    }
  }, [formData?.basicInformation?.startDate, formData?.parkVisitPlans?.parks, selected]);

  const hasStrategy = showStrategies.length > 0;

  return (
    <div className="space-y-6">
      {hasStrategy && (
        <>
          <p className="text-sm text-textPrimary">
            Choose your must-see shows and fireworks!
            <br />
            For any questions or details on a specific show, please contact your
            Travel Agent.
          </p>

          <button
            type="button"
            onClick={() => setShowAllShows(!showAllShows)}
            className="px-4 py-2 text-sm text-white bg-blue-600 rounded hover:bg-blue-700"
          >
            {showAllShows ? "Show Strategy Matches Only" : "Show All Shows"}
          </button>
        </>
      )}

      {hasStrategy &&
        selectedParks.map((park) => {
          const shows = showOptionsByPark[park] || [];
          const filtered = filterShowsByStrategy(shows);
          if (!filtered.length) return null;

          return (
            <div key={park} className="p-4 rounded-md border bg-white/80">
              <h3 className="mb-2 text-lg font-bold text-textPrimary">
                {park}
              </h3>
              <div className="space-y-2">
                {filtered.map((show) => {
                  const isSelected =
                    Array.isArray(selected) && selected.includes(show.name);

                  const { available, reason, rule } = getOptionAvailability(show, {
                    tripStartDate: tripStart,
                    optionType: "show",
                  });
                  const isDisabled = !available;
                  const closed = rule === "closure";
                  const tooEarly = rule === "opening";

                  return (
                    <label
                      key={show.name}
                      className="flex gap-2 items-start text-sm text-textPrimary"
                      aria-disabled={isDisabled}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={isDisabled}
                        onChange={() => !isDisabled && handleToggle(show.name)}
                        className={`mt-1 ${isDisabled ? "opacity-50" : ""}`}
                        aria-disabled={isDisabled}
                        title={!available ? reason : undefined}
                      />
                      <div className="flex flex-col items-start gap-0.5">
                        <div className="flex gap-1 items-center">
                          <span className={isDisabled ? "opacity-50" : ""}>
                            {show.name}
                          </span>

                          {show.showInfoNote && (
                            <Tooltip message={show.showInfoNote}>
                              <span className="text-textPrimary cursor-pointer text-[10px] border border-textPrimary rounded-full px-1 pb-[1px] leading-none">
                                ?
                              </span>
                            </Tooltip>
                          )}

                          {show.warningNote && (
                            <Tooltip message={show.warningNote}>
                              <span className="text-red-600 cursor-pointer text-[10px] border border-red-600 rounded-full px-[0.3rem] pb-[1px] leading-none ml-1">
                                !
                              </span>
                            </Tooltip>
                          )}
                        </div>

                        <span className="text-xs text-gray-500">
                          ({show.type})
                        </span>

                        {!available && closed && (
                          <span className="text-xs text-red-500">
                            This show may be <strong>closed</strong> during your
                            trip for refurbishment or removal.
                          </span>
                        )}

                        {!available && tooEarly && (
                          <span className="text-xs text-yellow-600">
                            This show may <strong>not be open yet</strong>{" "}
                            during your trip.
                          </span>
                        )}

                        {!available && !closed && !tooEarly && reason && (
                          <span className="text-xs text-amber-600" title={reason}>
                            {reason}
                          </span>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}

      {selected?.length > 0 && (
        <p className="mt-3 text-sm text-gray-600">
          <strong>To review your selections, click Next.</strong>
        </p>
      )}

      {!formData.showFireworkStrategy?.showStrategy?.includes("Not Interested") &&
        (!selected || selected.length === 0) && (
          <p className="mt-3 text-sm italic text-red-500">
            No shows or fireworks selected. Please choose at least one or go
            back and select "Not Interested" to skip.
          </p>
        )}
    </div>
  );
};

export default ShowSelector;